/* YOUR SET picker state — checkbox build (owner-approved 2026-07-29).
 *
 * Anchor pinned, each member an independent tick. Money rule (mirrors the
 * coupon strip and the pill): the discount floors PER ITEM, then payables
 * sum. floor(sum × pct) understates what Shopify charges by a cent on mixed
 * sets — 13195→6598 plus 3995→1998 is 8596, while floor(17190 × 50%) is 8595.
 *
 * Ownership: this module owns WHICH totals the pill shows (via data-set-*
 * attributes on the button); afroyla-atc-price-pill.js stays the only writer
 * of the pill's DOM. Verb ownership stays with afroyla-preorder.js. The ship
 * manifest listens to afroyla:set:change (kind 'solo' when nothing ticked,
 * 'set' otherwise) and explains any mixed ship states.
 *
 * Quick-view sheet: per-member <template> rendered by the snippet; this file
 * opens/closes it, paints the member price + ships split, and wires the ADD
 * button — which shows ONLY that member's payable and ticks its box.
 *
 * The submit intercept adds anchor + ticked members in ONE /cart/add call.
 * Members carry _attach_source: set-picker; pre-order members are decorated
 * by the afroyla-preorder-cart.js interceptor from the registry blobs.
 *
 * Document-level listeners are module-scoped and registered ONCE (see the
 * bottom of this file), not per `init()` call. The size-sibling section
 * swap in afroyla-material-cards.js replaces the picker's whole DOM subtree
 * with a fresh one and re-runs `init()` on it; registering keydown/coupon/
 * variant/click listeners on `document` inside `init()` would leave the
 * PREVIOUS swap's listeners attached forever (closed over a detached
 * picker), accumulating one stale set per swap (code review finding M2).
 * The per-section wiring — listeners bound directly to `picker`, `qv`,
 * `qvAdd`, `more`, and the `submit` listener on `form` itself — stays
 * per-`init()` as before: those are element-scoped, so they are garbage
 * collected along with the element they were attached to. */
(function () {
  'use strict';

  // One instance descriptor per initialized picker, so the module-level
  // listeners below can reach every live picker's state without closing
  // over any single one of them at document-listener registration time.
  var instances = [];

  function liveInstances() {
    return instances.filter(function (inst) {
      return document.body.contains(inst.picker);
    });
  }

  function strip(scope) {
    return scope.querySelector('[data-coupon-strip]');
  }

  function isClaimed(scope) {
    var s = strip(scope);
    if (!s) return false;
    if (s.getAttribute('data-state') === 'claimed') return true;
    var code = s.getAttribute('data-coupon-code');
    if (!code) return false;
    try {
      return window.localStorage.getItem('afroyla:coupon:' + code) === '1';
    } catch (e) {
      return false;
    }
  }

  function savings(cents, scope) {
    var s = strip(scope);
    if (!s) return 0;
    var fixed = parseInt(s.getAttribute('data-fixed-cents') || '0', 10);
    var pct = parseFloat(s.getAttribute('data-discount-pct') || '0');
    var save = 0;
    if (fixed > 0) save = fixed;
    else if (pct > 0) save = Math.floor((cents * pct) / 100);
    return Math.min(save, cents);
  }

  function money(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function scopeOf(node) {
    return node.closest('.shopify-section') || node.closest('[data-section-id]') || document;
  }

  function init(picker) {
    if (picker.__init) return;
    picker.__init = true;

    var scope = scopeOf(picker);
    var island = picker.querySelector('[data-set-members-data]');
    var members = {};
    try {
      members = JSON.parse(island.textContent);
    } catch (e) {
      return;
    }

    function anchor() {
      return {
        vid: parseInt(picker.getAttribute('data-anchor-vid'), 10),
        cents: parseInt(picker.getAttribute('data-anchor-cents') || '0', 10),
        compare: parseInt(picker.getAttribute('data-anchor-compare') || '0', 10),
        pre: picker.getAttribute('data-anchor-pre') === 'true',
        eta: picker.getAttribute('data-anchor-eta') || ''
      };
    }

    function checkedIdxs() {
      var out = [];
      picker.querySelectorAll('[data-set-member]').forEach(function (row) {
        var input = row.querySelector('[data-set-member-input]');
        if (input && input.checked) out.push(row.getAttribute('data-member-idx'));
      });
      return out;
    }

    function checkedMembers() {
      return checkedIdxs().map(function (i) { return members[i]; }).filter(Boolean);
    }

    // Ticked upsell rows (checkbox build, owner decision 2026-08-21) ride the
    // same add and the same totals. afroyla-pdp-upsell.js exposes them; when
    // it is absent (or nothing is ticked) this is just [].
    function upsellRiders() {
      return typeof window.AfroylaUpsellRiders === 'function'
        ? window.AfroylaUpsellRiders(scope)
        : [];
    }

    function payableOf(cents) {
      return cents - savings(cents, scope);
    }

    // One price fragment: payable + struck reference. Claimed strikes the
    // base; idle strikes compare_at only when it is a real markup.
    function paintPriceEl(el, cents, compare, prefix) {
      if (!el) return;
      var claimed = isClaimed(scope);
      var show = claimed ? payableOf(cents) : cents;
      var strike = claimed ? cents : (compare > cents ? compare : 0);
      el.textContent = '';
      el.appendChild(document.createTextNode((prefix || '') + money(show)));
      if (strike && strike !== show) {
        var s = document.createElement('s');
        s.textContent = money(strike);
        el.appendChild(s);
      }
    }

    function totals() {
      var a = anchor();
      var items = [a].concat(checkedMembers()).concat(upsellRiders());
      var base = 0, payable = 0;
      items.forEach(function (it) {
        base += it.cents;
        payable += payableOf(it.cents);
      });
      return { base: base, payable: payable, count: items.length };
    }

    function paintAll() {
      var a = anchor();
      paintPriceEl(picker.querySelector('[data-set-anchor-price]'), a.cents, a.compare, '');
      picker.querySelectorAll('[data-set-member]').forEach(function (row) {
        var idx = row.getAttribute('data-member-idx');
        var m = members[idx];
        if (!m) return;
        paintPriceEl(row.querySelector('[data-set-member-price]'), m.cents, m.compare, '+ ');
        var input = row.querySelector('[data-set-member-input]');
        row.classList.toggle('is-checked', !!(input && input.checked));
      });

      var totalEl = picker.querySelector('[data-set-total]');
      var t = totals();
      if (totalEl) {
        if (t.count > 1) {
          totalEl.innerHTML = '';
          totalEl.appendChild(document.createTextNode('Your set — ' + t.count + ' pieces, '));
          var b = document.createElement('strong');
          b.textContent = money(isClaimed(scope) ? t.payable : t.base);
          totalEl.appendChild(b);
          totalEl.appendChild(document.createTextNode('. The code covers every piece.'));
          totalEl.removeAttribute('hidden');
        } else {
          totalEl.setAttribute('hidden', '');
        }
      }
    }

    function broadcast() {
      var t = totals();
      var kind = t.count > 1 ? 'set' : 'solo';
      scope.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
        if (kind === 'solo') {
          btn.removeAttribute('data-set-base-cents');
          btn.removeAttribute('data-set-payable-cents');
        } else {
          btn.setAttribute('data-set-base-cents', t.base);
          btn.setAttribute('data-set-payable-cents', t.payable);
        }
      });
      document.dispatchEvent(new CustomEvent('afroyla:set:price', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('afroyla:set:change', {
        bubbles: true,
        detail: { picker: picker, kind: kind, anchor: anchor(), members: checkedMembers() }
      }));
    }

    function refresh() {
      paintAll();
      broadcast();
    }

    /* ---- quick-view sheet ---- */

    var qv = picker.querySelector('[data-set-qv]');
    var qvSlot = qv && qv.querySelector('[data-set-qv-slot]');
    var qvAdd = qv && qv.querySelector('[data-set-qv-add]');
    var qvIdx = null;

    function shipsLine(m) {
      var a = anchor();
      var aShort = (picker.getAttribute('data-anchor-short') || 'piece').toLowerCase();
      if (m.pre) return m.eta ? m.eta + ' (pre-order)' : 'With the next batch (pre-order)';
      if (a.pre) return 'Now — your ' + aShort + ' follows' + (a.eta ? ' ' + a.eta : '');
      return 'Now';
    }

    function paintQvAdd() {
      if (!qvAdd || qvIdx === null) return;
      var m = members[qvIdx];
      var row = picker.querySelector('[data-set-member][data-member-idx="' + qvIdx + '"]');
      var input = row && row.querySelector('[data-set-member-input]');
      if (input && input.checked) {
        qvAdd.textContent = 'Added ✓';
        qvAdd.classList.add('is-added');
      } else {
        var cents = isClaimed(scope) ? payableOf(m.cents) : m.cents;
        qvAdd.textContent = 'Add — ' + money(cents);
        qvAdd.classList.remove('is-added');
      }
    }

    function openQv(idx) {
      if (!qv || !qvSlot || !members[idx]) return;
      var tpl = picker.querySelector('[data-set-qv-tpl="' + idx + '"]');
      if (!tpl) return;
      qvIdx = idx;
      qvSlot.innerHTML = '';
      qvSlot.appendChild(tpl.content.cloneNode(true));
      var m = members[idx];
      paintPriceEl(qvSlot.querySelector('[data-qv-price]'), m.cents, m.compare, '');
      var ships = qvSlot.querySelector('[data-qv-ships]');
      if (ships) ships.textContent = shipsLine(m);
      paintQvAdd();
      qv.removeAttribute('hidden');
      document.documentElement.classList.add('afroyla-set-qv-open');
    }

    function closeQv() {
      if (!qv) return;
      qv.setAttribute('hidden', '');
      qvIdx = null;
      document.documentElement.classList.remove('afroyla-set-qv-open');
    }

    if (qv) {
      qv.addEventListener('click', function (event) {
        if (event.target.closest('[data-set-qv-close]')) closeQv();
      });
      // Escape-to-close is handled by the module-level keydown listener
      // below (see `instances`), not registered here — see file banner.
      if (qvAdd) {
        qvAdd.addEventListener('click', function () {
          if (qvIdx === null) return;
          var row = picker.querySelector('[data-set-member][data-member-idx="' + qvIdx + '"]');
          var input = row && row.querySelector('[data-set-member-input]');
          if (input && !input.checked) {
            input.checked = true;
            refresh();
            paintQvAdd();
            // Let the ✓ state land before the sheet slides away.
            window.setTimeout(closeQv, 450);
          } else {
            closeQv();
          }
        });
      }
    }

    /* ---- row interactions ---- */

    picker.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-set-qv-open]');
      if (opener) {
        // Inside a <label>: stop the click from also toggling the checkbox.
        event.preventDefault();
        event.stopPropagation();
        openQv(opener.getAttribute('data-set-qv-open'));
      }
    });

    picker.addEventListener('change', function (event) {
      if (event.target.matches('[data-set-member-input]')) refresh();
    });

    // "Show N more matching pieces" — reveal the rest of the family once.
    var more = picker.querySelector('[data-set-more]');
    if (more) {
      more.addEventListener('click', function () {
        picker.querySelectorAll('.afroyla-set__row--extra').forEach(function (r) {
          r.removeAttribute('hidden');
        });
        more.remove();
      });
    }

    /* Claiming and unclaiming are the same repaint. Listening only to the
     * claim left these rows quoting a discount the shopper had just handed
     * back through the drawer's ×, while the headline and the button had
     * already gone home to full price. Wired via the module-level
     * afroyla:coupon:claimed/afroyla:discount:* listener below — see file
     * banner — which calls `onCouponEvent` on every live instance. */
    function onCouponEvent() {
      refresh();
      if (qvIdx !== null) {
        var m = members[qvIdx];
        paintPriceEl(qvSlot.querySelector('[data-qv-price]'), m.cents, m.compare, '');
        paintQvAdd();
      }
    }

    // Variant switch: refresh the anchor's price and pre-order facts from the
    // event + the interceptor's registry, then recompute everything. Wired
    // via the module-level theme:variant:change listener below.
    function onVariantChange(event) {
      if (!scope.contains(event.target)) return;
      var variant = event.detail && event.detail.variant;
      if (!variant) return;
      picker.setAttribute('data-anchor-vid', variant.id);
      if (typeof variant.price === 'number') picker.setAttribute('data-anchor-cents', variant.price);
      picker.setAttribute('data-anchor-compare', variant.compare_at_price || 0);
      var reg = null;
      document.querySelectorAll('script[data-afroyla-preorder]').forEach(function (blob) {
        try {
          var data = JSON.parse(blob.textContent);
          if (data[String(variant.id)]) reg = data[String(variant.id)];
        } catch (e) {}
      });
      picker.setAttribute('data-anchor-pre', reg ? 'true' : 'false');
      picker.setAttribute('data-anchor-eta', reg && reg.eta ? reg.eta : '');
      refresh();
    }

    // Multi-add: one /cart/add call for anchor + every ticked member.
    //
    // The theme adds to cart from the BUTTON CLICK, not the form submit —
    // measured in production 30.07: clicking ATC fires no submit event at
    // all, so a submit-only interceptor is dead code on the real purchase
    // path and every ticked member was silently dropped (set-picker attach
    // count: zero across 187 live orders). The interception now happens at
    // the click in capture phase, before the theme's own listener; the
    // submit hook stays for keyboard-initiated submits.
    var form = scope.querySelector('form[data-product-form]');

    function multiAdd(event) {
      var picked = checkedMembers();
      var riders = upsellRiders();
      if (!picked.length && !riders.length) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      var qtyInput = form.querySelector('input[name="quantity"]');
      var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      var items = [{ id: anchor().vid, quantity: qty }];
      picked.forEach(function (m) {
        items.push({ id: m.vid, quantity: 1, properties: { _attach_source: 'set-picker' } });
      });
      riders.forEach(function (r) {
        items.push({ id: r.vid, quantity: 1, properties: { _attach_source: r.source || 'pdp-upsell' } });
      });

      var btn = form.querySelector('[data-add-to-cart]');
      if (btn) btn.classList.add('is-loading');

      fetch(window.theme && window.theme.routes && window.theme.routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add failed: ' + res.status);
          return res.json();
        })
        .then(function () {
          if (typeof window.AfroylaUpsellUncheck === 'function') window.AfroylaUpsellUncheck(scope);
          refresh();
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('theme:product:added', { bubbles: true }));
        })
        .catch(function () {
          // Fall back to the theme's own single-add path so the shopper is
          // never stuck: untick everything and re-submit without interception.
          picker.querySelectorAll('[data-set-member-input]').forEach(function (i) { i.checked = false; });
          if (typeof window.AfroylaUpsellUncheck === 'function') window.AfroylaUpsellUncheck(scope);
          refresh();
          if (form.requestSubmit) form.requestSubmit();
        })
        .then(function () {
          if (btn) btn.classList.remove('is-loading');
        });
    }

    if (form) {
      // The click-capture intercept that pre-empts the theme's own add path
      // (see the comment above `multiAdd`) is registered once at module
      // scope below and dispatches to `multiAdd` through the instance
      // descriptor pushed at the end of this function — not here — so a
      // stale form from a previous section swap can never receive it.
      form.addEventListener('submit', multiAdd, true);
    }

    instances.push({
      picker: picker,
      scope: scope,
      qv: qv,
      qvSlot: qvSlot,
      form: form,
      members: members,
      getQvIdx: function () { return qvIdx; },
      refresh: refresh,
      closeQv: closeQv,
      paintPriceEl: paintPriceEl,
      onCouponEvent: onCouponEvent,
      onVariantChange: onVariantChange,
      multiAdd: multiAdd
    });

    refresh();
  }

  // ---- module-level document listeners (registered once) -----------------
  //
  // These replace what used to be four separate document.addEventListener
  // calls inside init() (keydown Escape, three coupon events, theme:variant:
  // change, and the multi-add click-capture). Each now fans out to every
  // still-attached picker instance instead of closing over one picker at
  // registration time — see the file banner for why that mattered.
  if (!window.__aflSetPickerDocInit) {
    window.__aflSetPickerDocInit = true;

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      liveInstances().forEach(function (inst) {
        if (inst.qv && !inst.qv.hasAttribute('hidden')) inst.closeQv();
      });
    });

    ['afroyla:coupon:claimed', 'afroyla:discount:removed', 'afroyla:discount:applied'].forEach(
      function (name) {
        document.addEventListener(name, function () {
          liveInstances().forEach(function (inst) {
            inst.onCouponEvent();
          });
        });
      }
    );

    document.addEventListener('theme:variant:change', function (event) {
      liveInstances().forEach(function (inst) {
        inst.onVariantChange(event);
      });
    });

    // Ticked upsell rows count into totals — retotal when they change.
    document.addEventListener('afroyla:riders:change', function () {
      liveInstances().forEach(function (inst) {
        inst.refresh();
      });
    });

    // Multi-add click-capture: the theme adds to cart from the BUTTON CLICK,
    // not the form submit (see the comment above `multiAdd`), so this must
    // run in the capture phase before the theme's own listener.
    document.addEventListener('click', function (event) {
      var btn = event.target.closest && event.target.closest('[data-add-to-cart]');
      if (!btn) return;
      liveInstances().forEach(function (inst) {
        if (!inst.form) return;
        if (btn.closest('form[data-product-form]') !== inst.form) return;
        inst.multiAdd(event);
      });
    }, true);
  }

  function adoptAll(root) {
    (root || document).querySelectorAll('[data-set-picker]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { adoptAll(document); });
  } else {
    adoptAll(document);
  }

  // Sibling-size navigation swaps the whole product section in place, so a
  // fresh picker can arrive long after load.
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-set-picker]')) init(node);
          else if (node.querySelector && node.querySelector('[data-set-picker]')) adoptAll(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
